/**
 * PHASE 6 — thin WebGL2 session wrapper.
 *
 * Owns the canvas/context and the render loop plumbing. Everything browser
 * specific lives here; the math is elsewhere (mat4.ts, projection.ts). Only
 * computeCanvasSize is pure (unit-tested); the rest is glue.
 */

export interface DrawSize {
  widthPx: number;
  heightPx: number;
  dpr: number;
}

/** Device-pixel canvas size from CSS client size, dpr capped at 2. */
export function computeCanvasSize(clientWidth: number, clientHeight: number, dpr: number): DrawSize {
  const effective = Math.max(0.25, Math.min(dpr, 2));
  return {
    widthPx: Math.max(1, Math.round(clientWidth * effective)),
    heightPx: Math.max(1, Math.round(clientHeight * effective)),
    dpr: effective,
  };
}

export class GlSession {
  readonly canvas: HTMLCanvasElement;
  readonly gl: WebGL2RenderingContext | null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.gl = canvas.getContext("webgl2", {
      // Alpha channel kept so the canvas can composite over the camera feed
      // in AR mode (phase 7); normal sky mode clears opaquely regardless.
      alpha: true,
      premultipliedAlpha: true,
      antialias: true,
      depth: true,
      stencil: false,
      powerPreference: "high-performance",
    });
    if (!this.gl) {
      console.warn("WebGL2 not available; the app will fall back to CPU rendering.");
    }
  }

  get available(): boolean {
    return this.gl !== null;
  }

  /** Fit the drawing buffer to the element size, set the viewport. */
  resize(dpr = (typeof devicePixelRatio === "number" ? devicePixelRatio : 1)): DrawSize {
    const gl = this.gl;
    if (!gl) return { widthPx: 1, heightPx: 1, dpr: 1 };
    const size = computeCanvasSize(this.canvas.clientWidth, this.canvas.clientHeight, dpr);
    if (this.canvas.width !== size.widthPx || this.canvas.height !== size.heightPx) {
      this.canvas.width = size.widthPx;
      this.canvas.height = size.heightPx;
    }
    gl.viewport(0, 0, size.widthPx, size.heightPx);
    return size;
  }

  clear(r = 0, g = 0, b = 0, a = 1): void {
    const gl = this.gl;
    if (!gl) return;
    gl.clearColor(r, g, b, a);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
  }

  /**
   * Compile vertex + fragment shaders and link a program. Returns null and
   * logs the info log (console.warn) on failure.
   */
  createProgram(vertexSource: string, fragmentSource: string): WebGLProgram | null {
    const gl = this.gl;
    if (!gl) return null;
    const vs = compile(gl, gl.VERTEX_SHADER, vertexSource);
    const fs = compile(gl, gl.FRAGMENT_SHADER, fragmentSource);
    if (!vs || !fs) return null;
    const program = gl.createProgram();
    if (!program) return null;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const log = gl.getProgramInfoLog(program);
      console.warn(`Shader program link failed: ${log}`);
      gl.deleteProgram(program);
      return null;
    }
    return program;
  }

  dispose(): void {
    const gl = this.gl;
    if (!gl) return;
    const lose = gl.getExtension("WEBGL_lose_context");
    if (lose) lose.loseContext();
  }
}

function compile(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    console.warn(`Shader compile failed: ${log}`);
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}